import { useState, useRef, useEffect } from "react";

// DropdownContainer will be used to position our dropdown outside normal layout constraints
const DropdownContainer = ({ children, style }) => {
  return (
    <div
      className="fixed"
      style={{
        ...style,
        pointerEvents: "auto",
        zIndex: 9,
      }}
    >
      {children}
    </div>
  );
};

const SmartDropdown = ({
  isOpen,
  triggerRef,
  children,
  options = {},
  onClose,
}) => {
  const [position, setPosition] = useState("bottom");
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);

  const {
    width = 150,
    estimatedHeight = 150,
    align = "right", // 'right', 'left', or 'center'
  } = options;

  // Function to calculate and set dropdown position
  const updatePosition = () => {
    if (!isOpen || !triggerRef.current || !dropdownRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownHeight = dropdownRef.current.offsetHeight || estimatedHeight;

    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const useTopPosition = spaceBelow < dropdownHeight;

    // Calculate horizontal position based on alignment
    let left;
    switch (align) {
      case "left":
        left = rect.left;
        break;
      case "center":
        left = rect.left + rect.width / 2 - width / 2;
        break;
      case "right":
      default:
        left = rect.right - width;
        break;
    }

    // Ensure dropdown doesn't overflow viewport horizontally
    left = Math.max(10, Math.min(viewportWidth - width - 10, left));

    const newCoords = {
      left,
      // Position either above or below depending on available space
      top: useTopPosition ? rect.top - dropdownHeight : rect.bottom,
    };

    setCoords(newCoords);
    setPosition(useTopPosition ? "top" : "bottom");
  };

  // Calculate and set dropdown position when it opens
  useEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        if (onClose) onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Reposition on window resize and scroll
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    // Initial position
    updatePosition();

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <DropdownContainer 
      style={{
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        width: `${width}px`,
      }}
    >
      <div
        ref={dropdownRef}
        className="bg-white rounded shadow-xl overflow-hidden"
        data-position={position}
      >
        {children}
      </div>
    </DropdownContainer>
  );
};

export default SmartDropdown;
